import {
  createSlice,
  createSelector,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTransportsQuery } from './etlIntegrationsApi';

type ResourcePanelView =
  | 'new'
  | 'list_requests'
  | 'air_ambulance'
  | 'towpro'
  | 'norfolk_southern';

/** Panel views that show a specific resource type detail, form, or status. */
type ResourceRequestDetailView = Exclude<
  ResourcePanelView,
  'new' | 'list_requests'
>;

interface ResourceRequestsState {
  selectedRequest: string | null;
  isResourcePanelOpen: boolean;
  isEditing: boolean;
  resourcePanelView: ResourcePanelView;
  seenTransportIds: string[];
  breadCumbs: ResourcePanelView[];
}

export const resourceRequestsInitialState: ResourceRequestsState = {
  selectedRequest: null,
  isResourcePanelOpen: false,
  isEditing: false,
  resourcePanelView: 'list_requests',
  seenTransportIds: [],
  breadCumbs: [],
};

const initialState = resourceRequestsInitialState;

type StateWithResourceRequests = {
  resourceRequests?: Partial<ResourceRequestsState>;
};

const selectResourceRequestsSlice = (state: StateWithResourceRequests) =>
  state.resourceRequests;

const mergeResourceRequestsState = (
  resourceRequests: Partial<ResourceRequestsState> | undefined,
): ResourceRequestsState => {
  const merged = { ...initialState, ...resourceRequests };
  return {
    ...merged,
    seenTransportIds: merged.seenTransportIds ?? [],
    breadCumbs: merged.breadCumbs ?? [],
  };
};

/**
 * Merges partial or legacy persisted resourceRequests state with defaults.
 * Array fields are normalized so explicit undefined or null cannot leak through.
 */
export const selectResourceRequestsState = createSelector(
  [selectResourceRequestsSlice],
  mergeResourceRequestsState,
);

export const selectSelectedRequestId = createSelector(
  [selectResourceRequestsState],
  state => state.selectedRequest,
);

export const selectIsResourcePanelOpen = createSelector(
  [selectResourceRequestsState],
  state => state.isResourcePanelOpen,
);

export const selectIsEditing = createSelector(
  [selectResourceRequestsState],
  state => state.isEditing,
);

export const selectResourcePanelView = createSelector(
  [selectResourceRequestsState],
  state => state.resourcePanelView,
);

export const selectSeenTransportIds = createSelector(
  [selectResourceRequestsState],
  state => state.seenTransportIds,
);

export const selectBreadCrumbs = createSelector(
  [selectResourceRequestsState],
  state => state.breadCumbs,
);

/**
 * The result of the getTransports query, which should be an array of transports.
 */
type TransportQueryResult = {
  transports?: {
    gmr_transport_id: unknown;
  }[];
};

const getTransportId = (transport: unknown): string | undefined => {
  if (transport === null || typeof transport !== 'object') {
    return undefined;
  }

  const transportRecord = transport as {
    gmr_transport_id: unknown;
  };

  const transportId = transportRecord.gmr_transport_id;
  if (typeof transportId !== 'string' || transportId.trim().length === 0) {
    return undefined;
  }

  return transportId;
};

/**
 * Extract non-empty transport ids from the GMR transport response.
 */
const getTransportIds = (result: TransportQueryResult): string[] => {
  if (!Array.isArray(result.transports)) {
    return [];
  }

  return result.transports
    .map(transport => getTransportId(transport))
    .filter((transportId): transportId is string => Boolean(transportId));
};

/**
 * Manages the UI state for the right-hand resource requests panel. See
 * etlIntegrationsApi & hooks for the actual resource request data (e.g. GMR/Air
 * Ambulance transports) are cached under the api key in redux.
 */
const resourceRequestsSlice = createSlice({
  name: 'resourceRequests',
  initialState,
  reducers: {
    setSelectedRequest: (
      state,
      action: PayloadAction<{
        requestId: string;
        detailView: ResourceRequestDetailView;
      } | null>,
    ) => {
      state.selectedRequest = action.payload?.requestId ?? null;
      state.isEditing = false;

      if (action.payload) {
        state.breadCumbs = ['list_requests', action.payload.detailView];
      }
    },
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    openResourcePanel: state => {
      state.isResourcePanelOpen = true;
      state.resourcePanelView = 'list_requests';
      state.breadCumbs = [...initialState.breadCumbs];
    },
    closeResourcePanel: state => {
      state.isResourcePanelOpen = false;
      state.selectedRequest = null;
      state.isEditing = false;
      state.resourcePanelView = 'list_requests';
      state.breadCumbs = [...initialState.breadCumbs];
    },
    setResourcePanelView: (state, action: PayloadAction<ResourcePanelView>) => {
      if (action.payload === 'new') {
        state.resourcePanelView = 'new';
        state.breadCumbs = ['list_requests', 'new'];
        return;
      }

      state.resourcePanelView = action.payload;

      const index = state.breadCumbs.indexOf(action.payload);
      if (index > -1) {
        // If navigating to a previous bread crumb remove any bread crumbs
        // afterwards
        state.breadCumbs = state.breadCumbs.slice(0, index + 1);
      } else {
        // Add a new bread crumb
        state.breadCumbs = [...state.breadCumbs, action.payload];
      }
    },
    markTransportSeen: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.seenTransportIds)) {
        state.seenTransportIds = [];
      }
      if (!state.seenTransportIds.includes(action.payload)) {
        state.seenTransportIds.push(action.payload);
      }
    },
    toggleResourcePanel: state => {
      state.isResourcePanelOpen = !state.isResourcePanelOpen;
      if (state.isResourcePanelOpen && state.breadCumbs.length <= 0) {
        state.breadCumbs = [state.resourcePanelView];
      }
    },
  },
});

export const {
  setSelectedRequest,
  setIsEditing,
  openResourcePanel,
  closeResourcePanel,
  setResourcePanelView,
  toggleResourcePanel,
  markTransportSeen,
} = resourceRequestsSlice.actions;

export const useSelectedRequestId = () => useSelector(selectSelectedRequestId);
export const useIsResourcePanelOpen = () =>
  useSelector(selectIsResourcePanelOpen);
export const useIsEditingResourceRequest = () => useSelector(selectIsEditing);
export const useResourcePanelView = () => useSelector(selectResourcePanelView);
export const useSeenTransportIds = () => useSelector(selectSeenTransportIds);
export const useBreadCrumbs = () => useSelector(selectBreadCrumbs);
/**
 * Returns true when there are transport requests not yet marked as seen.
 * This checks only the GMR transport feed used by the portal
 * resource request panel, because that is the current source of truth for
 * unread request indicators.
 */
export const useHasUnreadTransportRequests = (): boolean => {
  const seenTransportIds = useSelector(selectSeenTransportIds);
  const { data, currentData, isError } = useGetTransportsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const transportsResult = useMemo(() => {
    if (!currentData && !data) {
      return null;
    }
    return currentData ?? data;
  }, [currentData, data]);

  const transportIds = useMemo(() => {
    if (!transportsResult) {
      return [];
    }
    return getTransportIds(transportsResult);
  }, [transportsResult]);

  return useMemo(() => {
    if (isError && !transportsResult) {
      return false;
    }

    if (transportIds.length === 0) {
      return false;
    }

    const seenTransportIdsSet = new Set(seenTransportIds);

    return transportIds.some(
      transportId => !seenTransportIdsSet.has(transportId),
    );
  }, [isError, seenTransportIds, transportIds, transportsResult]);
};

export type { ResourcePanelView, ResourceRequestDetailView };
export const resourceRequests = resourceRequestsSlice.reducer;
